import { ensureCarestreamVersion } from "./auto-updating";
import { Environment, getConfig } from "./config";
import {
  bulkUploadPatients,
  bulkUploadPatientsChunked,
} from "./message-handlers/bulk-upload-patients";
import { bulkUploadAppointmentsChunked } from "./message-handlers/bulk-upload-appointment";
import { bulkUploadAppointmentStatus } from "./message-handlers/bulk-upload-appointment-status";
import {
  uploadQuestionnaireAnswers,
  uploadQuestionnaireQuestions,
} from "./message-handlers/debug";
import { patientSync } from "./message-handlers/patient-sync";
import { R4Message } from "./R4Message";
import { buildApplicationScope, migrateLocalDb } from "./server";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

export const start = async (environment: Environment, licenseKey: string) => {
  const config = getConfig(environment);

  if (config.migrateDb) await migrateLocalDb(config);

  console.log("Starting to initialise");

  const applicationScope = await buildApplicationScope(config, licenseKey);

  applicationScope.logging.log({
    message: "Application scope initialised",
    licenseKey,
    sqsQueueUrl: applicationScope.licenceKeyData.sqsQueueUrl,
  });

  const startupTime = Date.now();
  let lastHourlyCheck = 0;
  let maxPatientCodeUploaded = 0;
  let maxAppointmentUploaded = 0;

  // Main loop - SQS + Bulk Upload + Check Carestream
  // We crash out every hour, because this is the best way to avoid
  // memory leaks etc. killing us. It also means we don't need to worry about
  // refreshing the AWS credentials for SQS, which are valid for 36 hours.
  while (true) {
    try {
      const uptimeMs = Date.now() - startupTime;

      // Bail out if we've been up more than a day
      if (uptimeMs > ONE_DAY_MS) {
        process.exit(0);
        break;
      }

      /*
      Runs on startup, and every hour
       */
      if (Date.now() - lastHourlyCheck > ONE_HOUR_MS) {
        await ensureCarestreamVersion(applicationScope);
        maxPatientCodeUploaded = await bulkUploadPatientsChunked(
          applicationScope,
          maxPatientCodeUploaded
        );

        // Update Appointment to flynotes
        maxAppointmentUploaded = await bulkUploadAppointmentsChunked(
          applicationScope,
          maxAppointmentUploaded
        );

        // Update Appointment status to flynotes
        await bulkUploadAppointmentStatus(applicationScope);

        lastHourlyCheck = Date.now();
      }

      await applicationScope.sqs.processOneMessage(async (data: R4Message) => {
        switch (data.type) {
          case "test":
            applicationScope.logging.log({ message: "Test message received" });
            break;
          case "restart":
            applicationScope.logging.log({
              message: "Restart requested over SQS. Restarting in 5 seconds..",
            });
            setTimeout(() => process.exit(0), 5000);
            break;
          case "questionnaire-questions-upload":
            await uploadQuestionnaireQuestions(applicationScope);
            break;
          case "questionnaire-answers-upload":
            await uploadQuestionnaireAnswers(applicationScope);
            break;
          case "bulk-upload-patients":
            maxPatientCodeUploaded = await bulkUploadPatientsChunked(
              applicationScope,
              maxPatientCodeUploaded
            );
            break;
          case "patient-sync":
            await patientSync(applicationScope, data);
            break;
          case "bulk-upload-appointments":
            maxAppointmentUploaded = await bulkUploadAppointmentsChunked(
              applicationScope,
              maxAppointmentUploaded
            );
            break;
          case "bulk-upload-appointment-status":
            await bulkUploadAppointmentStatus(applicationScope);
            break;
          default:
            applicationScope.logging.log(
              { message: "Unexpected message format", data },
              "error"
            );
        }
      });
    } catch (e) {
      applicationScope.logging.log(
        {
          message: `Error in main processing loop: ${e.message}`,
          stack: e.stack,
        },
        "error"
      );

      throw e;
    }
  }
};
